import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '~/lib/utils';
import {
  getFullName,
  getInitials,
  MinimalProfile,
} from '~/lib/utils/profile-utils';

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-muted',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarFallback, AvatarImage };

const profileAvatarVariants = cva('', {
  variants: {
    variant: {
      light: 'bg-zinc-200 text-black',
      dark: 'bg-zinc-900 text-white',
    },
    size: {
      default: 'h-16 w-16 text-3xl',
      sm: 'h-10 w-10 text-lg',
      xs: 'size-8 text-sm',
    },
  },
  defaultVariants: {
    variant: 'light',
    size: 'default',
  },
});

export interface ProfileAvatarProps
  extends VariantProps<typeof profileAvatarVariants> {
  profile?: MinimalProfile;
  className?: string;
  fallbackClassName?: string;
  avatarClassName?: string;
}

export const ProfileAvatar = ({
  profile,
  size,
  variant,
  className,
  avatarClassName,
  fallbackClassName,
}: ProfileAvatarProps) => {
  return (
    <Avatar
      className={cn(
        'font-bold',
        profileAvatarVariants({ size, variant, className }),
      )}>
      <AvatarImage
        src={profile?.picture_url ?? ''}
        alt={getFullName(profile) ?? 'Profile picture'}
        className={cn('object-cover', avatarClassName)}
      />
      <AvatarFallback className={cn('bg-transparent', fallbackClassName)}>
        {getInitials(profile)}
      </AvatarFallback>
    </Avatar>
  );
};
